/**
 * Análisis de Ventas — página principal.
 * Sub-módulo de "G. Comercial" (después de Chatbot).
 * Reusa /api/chatbot/projects y /api/chatbot/stages para selector proyecto+etapa.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { analisisVentasAPI } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import {
  KpiCard, MatrizVelocidadesCard, MatrizMixCard, MatrizEstacionamientosCard,
  MatrizRecaudacionCard, MatrizEvaluacionesCard, PanelControlEditModal,
  EvolucionChart, ColocacionChart, EficienciaChart, HeatmapPisos,
  Card, FiltrosIcon,
} from './components';
import { DiagnosticoIA, ConsultorIA } from './ia';

const API = process.env.REACT_APP_BACKEND_URL || '';

const AnalisisVentas = () => {
  const [projects, setProjects] = useState([]);
  const [stages, setStages] = useState([]);
  const [tipologias, setTipologias] = useState([]);
  const [columnas, setColumnas] = useState([]);
  const [selProject, setSelProject] = useState('');
  const [selStage, setSelStage] = useState('all');
  const [selTip, setSelTip] = useState('all');
  const [selCol, setSelCol] = useState('all');
  const [dashboard, setDashboard] = useState(null);
  const [loadingDash, setLoadingDash] = useState(false);
  const [loadingColumnas, setLoadingColumnas] = useState(false);

  // Matrices adicionales (Recaudación / Evaluaciones)
  const [recaudacion, setRecaudacion] = useState(null);
  const [evaluaciones, setEvaluaciones] = useState(null);
  const [panelModalOpen, setPanelModalOpen] = useState(false);

  const { user } = useAuth();
  const esAdmin = user?.rol_nombre === 'Administrador';

  const authHeaders = {
    Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
  };

  // Cargar proyectos al montar
  useEffect(() => {
    axios
      .get(`${API}/api/chatbot/projects`, { headers: authHeaders })
      .then((r) => {
        const items = r.data?.items || [];
        setProjects(items);
      })
      .catch(() => toast.error('No se pudieron cargar los proyectos'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cargar etapas al cambiar proyecto
  useEffect(() => {
    if (!selProject) {
      setStages([]);
      setSelStage('all');
      return;
    }
    axios
      .get(`${API}/api/chatbot/stages`, {
        params: { codigo_proyecto: selProject },
        headers: authHeaders,
      })
      .then((r) => setStages(r.data?.items || []))
      .catch(() => setStages([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selProject]);

  // Cargar catálogo de tipologías al cambiar proyecto/etapa
  useEffect(() => {
    if (!selProject) {
      setTipologias([]);
      return;
    }
    const etapa = selStage === 'all' ? null : selStage;
    analisisVentasAPI
      .tipologias(selProject, etapa)
      .then((r) => setTipologias(r.data?.items || []))
      .catch(() => setTipologias([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selProject, selStage]);

  // Cargar catálogo de columnas al cambiar proyecto/etapa/tipología.
  // Cuando Tipología cambia, esta lista se filtra al subconjunto de columnas
  // que efectivamente contienen unidades de esa tipología.
  useEffect(() => {
    if (!selProject) {
      setColumnas([]);
      return;
    }
    const etapa = selStage === 'all' ? null : selStage;
    const tipologia = selTip === 'all' ? null : selTip;
    setLoadingColumnas(true);
    analisisVentasAPI
      .columnas(selProject, etapa, tipologia)
      .then((r) => setColumnas(r.data?.items || []))
      .catch(() => setColumnas([]))
      .finally(() => setLoadingColumnas(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selProject, selStage, selTip]);

  // Cargar dashboard + Recaudación + Evaluaciones en paralelo al cambiar
  // cualquier selector. Los 3 requests se disparan en paralelo con Promise.all
  // implícito (las promesas corren simultáneamente aunque cada una tenga su
  // .then), así que el usuario percibe solo la latencia del más lento.
  useEffect(() => {
    if (!selProject) {
      setDashboard(null);
      setRecaudacion(null);
      setEvaluaciones(null);
      return;
    }
    const etapa = selStage === 'all' ? null : selStage;
    const tip = selTip === 'all' ? null : selTip;
    const col = selCol === 'all' ? null : selCol;

    setLoadingDash(true);
    analisisVentasAPI
      .dashboard(selProject, etapa, tip, col)
      .then((r) => setDashboard(r.data))
      .catch((err) =>
        toast.error(err.response?.data?.detail || 'Error cargando dashboard'),
      )
      .finally(() => setLoadingDash(false));

    // Recaudación: acepta filtros de tipología/columna (aplicados solo a
    // Departamento en el backend). Cargamos en paralelo, sin bloquear el UI.
    analisisVentasAPI
      .recaudacion(selProject, etapa, tip, col)
      .then((r) => setRecaudacion(r.data))
      .catch(() => setRecaudacion(null));

    // Evaluaciones: acepta filtros; el backend decide si aplicar Panel/Alerta/Margen.
    analisisVentasAPI
      .evaluaciones(selProject, etapa, tip, col)
      .then((r) => setEvaluaciones(r.data))
      .catch(() => setEvaluaciones(null));
  }, [selProject, selStage, selTip, selCol]);

  // Handler para guardar el Panel de Control (solo Admin)
  const handleSavePanel = async ({ codigo_proyecto, codigo_etapa, valor }) => {
    try {
      await analisisVentasAPI.updatePanelControl({
        codigo_proyecto, codigo_etapa, valor,
      });
      toast.success('Panel de Control actualizado.');
      // Refrescar evaluaciones
      const r = await analisisVentasAPI.evaluaciones(
        selProject,
        selStage === 'all' ? null : selStage,
        selTip === 'all' ? null : selTip,
        selCol === 'all' ? null : selCol,
      );
      setEvaluaciones(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error actualizando Panel de Control');
      throw err;
    }
  };

  const proyectoLabel = dashboard?.proyecto_nombre || '';

  return (
    <div className="skin-page min-h-screen py-4 px-3 sm:px-6" data-testid="av-page">
      {/* Badge flotante de carga (fixed al viewport) */}
      {loadingDash && (
        <div
          className="fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-2 rounded-full shadow-lg animate-in fade-in slide-in-from-top-2 duration-200"
          style={{ background: 'var(--skin-panel)', border: '1px solid var(--skin-accent)' }}
          data-testid="av-loading-overlay"
        >
          <Loader2 size={16} className="animate-spin" style={{ color: 'var(--skin-accent)' }} />
          <span className="text-xs font-medium" style={{ color: 'var(--skin-accent-dark)' }}>
            Actualizando datos…
          </span>
        </div>
      )}
      <div className="max-w-[1440px] mx-auto space-y-4">
        {/* Header */}
        <div className="skin-page-head pb-3" style={{ borderBottom: '1px solid var(--skin-line)' }}>
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
            <div>
              <h1 data-testid="av-title" style={{ fontSize: '24px' }}>
                Cuadro de Ventas
                {proyectoLabel && (
                  <>
                    {' · '}
                    <span className="italic" style={{ color: 'var(--skin-accent-dark)' }}>{proyectoLabel}</span>
                  </>
                )}
              </h1>
              <p>Reporte comercial</p>
            </div>
            {loadingDash && (
              <div className="text-xs skin-text-mute inline-flex items-center gap-1">
                <Loader2 size={12} className="animate-spin" /> Cargando…
              </div>
            )}
          </div>
        </div>

        {/* Selectores proyecto + etapa */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
              <Label className="skin-field-label">Proyecto</Label>
            <div className="relative">
              <Select
                value={selProject}
                onValueChange={(v) => {
                  // Reset sincrónico de filtros dependientes al cambiar de proyecto.
                  // Evita arrastrar "Etapa X" del proyecto anterior (que puede no
                  // existir en el nuevo) y el flash de "Sin datos" fantasma.
                  setSelProject(v);
                  setSelStage('all');
                  setSelTip('all');
                  setSelCol('all');
                  setDashboard(null);
                }}
              >
                <SelectTrigger data-testid="av-select-proyecto" className="skin-select">
                  <SelectValue placeholder="Selecciona un proyecto" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map((p) => (
                    <SelectItem key={p.codigo_proyecto} value={p.codigo_proyecto}>
                      {p.nombre_proyecto}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {loadingDash && (
                <Loader2
                  size={14}
                  className="animate-spin absolute right-8 top-1/2 -translate-y-1/2 pointer-events-none"
                  style={{ color: 'var(--skin-accent)' }}
                  data-testid="av-spinner-proyecto"
                />
              )}
            </div>
          </div>
          <div>
              <Label className="skin-field-label">Etapa</Label>
            <div className="relative">
              <Select
                value={selStage}
                onValueChange={(v) => {
                  // Al cambiar etapa, resetear filtros dependientes (Tip / Col)
                  // para que no queden colgados valores del catálogo anterior.
                  setSelStage(v);
                  setSelTip('all');
                  setSelCol('all');
                }}
                disabled={!stages.length}
              >
                <SelectTrigger data-testid="av-select-etapa" className="skin-select">
                  <SelectValue placeholder="Todas las etapas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las etapas</SelectItem>
                  {stages.map((s) => (
                    <SelectItem key={s.codigo_etapa || s.etapa} value={s.codigo_etapa || s.etapa}>
                      {s.nombre_etapa || s.codigo_etapa || s.etapa}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {loadingDash && (
                <Loader2
                  size={14}
                  className="animate-spin absolute right-8 top-1/2 -translate-y-1/2 pointer-events-none"
                  style={{ color: 'var(--skin-accent)' }}
                  data-testid="av-spinner-etapa"
                />
              )}
            </div>
          </div>
        </div>

        {!selProject ? (
          <div className="skin-panel p-8 text-center skin-text-soft">
            Selecciona un proyecto para ver el análisis de ventas.
          </div>
        ) : dashboard ? (
          <div className="relative space-y-4">
            {dashboard.kpis.total_filtro === 0 && (
              <div className="skin-warning mb-4">
                Sin datos para este proyecto/filtros. Verifica que exista información
                comercial en <code>comercial_v2.fact_cuadroventas</code>.
              </div>
            )}
            {/* Página 1: KPI + Filtros + Velocidades + Mix */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 items-stretch">
              <div className="lg:col-span-1 h-full">
                <KpiCard
                  avanceFiltro={dashboard.kpis.avance_filtro}
                  mediaProyecto={dashboard.kpis.media_proyecto}
                />
              </div>
              <div className="lg:col-span-1 h-full">
                <Card
                  icon={<FiltrosIcon size={14} />}
                  title="Filtros de Control Operativo"
                  className="h-full flex flex-col"
                >
                  <div className="space-y-3 flex-1">
                    <div>
                      <Label className="skin-field-label">
                        Segmentación por Tipología
                      </Label>
                      <div className="relative">
                        <Select value={selTip} onValueChange={(v) => {
                          // Al cambiar tipología: resetear columna y forzar
                          // recarga del catálogo de columnas filtrado por tipología.
                          // React 18 hace batching de estos setState en un solo render,
                          // por lo que dashboard + columnas se re-fetchean en paralelo.
                          setSelTip(v);
                          setSelCol('all');
                        }}>
                          <SelectTrigger data-testid="av-select-tipologia" className="skin-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">-- Todas las Tipologías --</SelectItem>
                            {tipologias.map((t) => (
                              <SelectItem key={t} value={t}>{t}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {loadingDash && (
                          <Loader2
                            size={14}
                            className="animate-spin absolute right-8 top-1/2 -translate-y-1/2 pointer-events-none"
                            style={{ color: 'var(--skin-accent)' }}
                            data-testid="av-spinner-tipologia"
                          />
                        )}
                      </div>
                    </div>
                    <div>
                      <Label className="skin-field-label">
                        Eje Arquitectónico (Columna)
                      </Label>
                      <div className="relative">
                        <Select value={selCol} onValueChange={setSelCol} disabled={loadingColumnas}>
                          <SelectTrigger data-testid="av-select-columna" className="skin-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">-- Todas las Líneas --</SelectItem>
                            {columnas.map((c) => (
                              <SelectItem key={c} value={c}>{c}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {(loadingDash || loadingColumnas) && (
                          <Loader2
                            size={14}
                            className="animate-spin absolute right-8 top-1/2 -translate-y-1/2 pointer-events-none"
                            style={{ color: 'var(--skin-accent)' }}
                            data-testid="av-spinner-columna"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
              <div className="lg:col-span-2 h-full">
                <MatrizVelocidadesCard
                  kpis={dashboard.kpis}
                  rows={dashboard.matriz_velocidades}
                  mesesOperativos={dashboard.meses_operativos}
                  primeraVentaAm={dashboard.primera_venta_am}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
              <div>
                <MatrizMixCard rows={dashboard.matriz_mix} />
              </div>
              <div>
                <MatrizEstacionamientosCard
                  rows={dashboard.matriz_estacionamientos}
                  mesesOperativos={dashboard.meses_operativos}
                  primeraVentaAm={dashboard.primera_venta_am}
                />
              </div>
            </div>

            {/* Fila NUEVA: Recaudación (izq) + Evaluaciones (der) */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
              <div>
                <MatrizRecaudacionCard payload={recaudacion} />
              </div>
              <div>
                <MatrizEvaluacionesCard
                  payload={evaluaciones}
                  esAdmin={esAdmin}
                  onEditPanel={() => setPanelModalOpen(true)}
                />
              </div>
            </div>

            {/* Página 2: Consultor IA + Diagnóstico */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <ConsultorIA
                proyecto={selProject}
                etapa={selStage === 'all' ? null : selStage}
                tipologia={selTip === 'all' ? null : selTip}
                columna={selCol === 'all' ? null : selCol}
              />
              <DiagnosticoIA
                proyecto={selProject}
                etapa={selStage === 'all' ? null : selStage}
                tipologia={selTip === 'all' ? null : selTip}
                columna={selCol === 'all' ? null : selCol}
                dashboardLoading={loadingDash}
              />
            </div>

            <EvolucionChart data={dashboard.evolucion_mensual} />

            {/* Página 3: Colocación + Eficiencia */}
            <ColocacionChart data={dashboard.evolucion_mensual} />
            <EficienciaChart
              data={dashboard.eficiencia_tipologia}
              benchmark={dashboard.heatmap.benchmark}
            />

            {/* Página 4: Heatmap */}
            <HeatmapPisos
              columnas={dashboard.heatmap.columnas_meta}
              rangos={dashboard.heatmap.rangos_pisos}
              celdas={dashboard.heatmap.celdas}
              benchmark={dashboard.heatmap.benchmark}
            />
          </div>
        ) : (
          <div className="skin-panel p-8 text-center skin-text-soft">
            {loadingDash ? 'Cargando dashboard…' : 'Sin datos.'}
          </div>
        )}
      </div>

      {/* Modal de edición del Panel de Control (solo Admin) */}
      <PanelControlEditModal
        open={panelModalOpen}
        onClose={() => setPanelModalOpen(false)}
        proyecto={selProject}
        etapa={selStage === 'all' ? null : selStage}
        valorActual={evaluaciones?.panel_control?.valor}
        onSaved={handleSavePanel}
      />
    </div>
  );
};

export default AnalisisVentas;
